import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { pendingImages } from '../EditableMarkdown';
import FigureImageView from './FigureImageView';

export interface FigureImageOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    figureImage: {
      setFigureImage: (options: {
        src: string;
        alt?: string;
        width?: number;
        caption?: string;
        layout?: 'full' | 'row';
      }) => ReturnType;
    };
  }
}

const FigureImage = Node.create<FigureImageOptions>({
  name: 'figureImage',
  group: 'block',
  draggable: true,
  isolating: true,
  atom: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      width: { default: 100 },
      caption: { default: '' },
      layout: { default: 'full' },
      cropX: { default: 0 },
      cropY: { default: 0 },
      cropW: { default: 100 },
      cropH: { default: 100 },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'figure',
        getAttrs(dom: HTMLElement) {
          const img = dom.querySelector('img');
          if (!img) return false;
          const figcaption = dom.querySelector('figcaption');
          const cropStr = dom.getAttribute('data-crop');
          const [cropX, cropY, cropW, cropH] = cropStr
            ? cropStr.split(',').map(Number)
            : [0, 0, 100, 100];
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt') || '',
            width: parseInt(dom.getAttribute('data-width') || '100', 10),
            caption: figcaption?.textContent || '',
            layout: dom.getAttribute('data-layout') || 'full',
            cropX,
            cropY,
            cropW,
            cropH,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, alt, width, caption, layout, ...rest } = HTMLAttributes;
    const figureAttrs = mergeAttributes(this.options.HTMLAttributes, rest, {
      'data-width': width,
      'data-layout': layout,
    });

    const imgNode = ['img', { src, alt }];
    const children: any[] = [imgNode];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }

    return ['figure', figureAttrs, ...children];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FigureImageView);
  },

  addCommands() {
    return {
      setFigureImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        props: {
          handlePaste(_view, event) {
            const items = Array.from(event.clipboardData?.items || []);
            const imageItem = items.find((item) => item.type.startsWith('image/'));
            if (!imageItem) return false;

            event.preventDefault();
            const file = imageItem.getAsFile();
            if (!file) return false;

            const blobUrl = URL.createObjectURL(file);
            pendingImages.set(blobUrl, { file });
            editor.chain().focus().setFigureImage({ src: blobUrl, alt: file.name }).run();
            return true;
          },
          handleDrop(_view, event) {
            const files = Array.from(event.dataTransfer?.files || []);
            const imageFile = files.find((f) => f.type.startsWith('image/'));
            if (!imageFile) return false;

            event.preventDefault();
            const blobUrl = URL.createObjectURL(imageFile);
            pendingImages.set(blobUrl, { file: imageFile });
            editor.chain().focus().setFigureImage({ src: blobUrl, alt: imageFile.name }).run();
            return true;
          },
        },
      }),
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const { src, alt, width, caption, layout, cropX, cropY, cropW, cropH } = node.attrs;
          const hasCrop = !(cropX === 0 && cropY === 0 && cropW === 100 && cropH === 100);
          const altAttr = alt ? ` alt="${alt}"` : '';
          const imgTag = `<img src="${src}"${altAttr} />`;
          const captionTag = caption
            ? `\n<figcaption>${caption}</figcaption>`
            : '';
          const cropAttr = hasCrop ? ` data-crop="${cropX},${cropY},${cropW},${cropH}"` : '';
          const html = `<figure data-width="${width}" data-layout="${layout}"${cropAttr}>\n${imgTag}${captionTag}\n</figure>`;
          state.write(html);
          state.closeBlock(node);
        },
        parse: {},
      },
    };
  },
});

export default FigureImage;
